import {
  collection, addDoc, getDocs, query, where, orderBy,
  QueryDocumentSnapshot, DocumentData, doc, updateDoc,
  runTransaction, Timestamp, limit, getDoc, onSnapshot,
  writeBatch, deleteField, getCountFromServer
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../lib/firebase';
import { secureLog } from '../utils/securityUtils';
import type { Trade } from '../types';
import { calculateLotSize, calculateRiskRewardRatio, calculateProfitLoss as calculateAssetProfitLoss } from '../utils/tradeCalculations';
import { mediaStorageService } from './mediaStorageService';
import { accountStatsService } from './accountStatsService';
import { runningBalanceService } from './runningBalanceService';
import { backgroundTaskService } from './backgroundTaskService';
import { BASIC_TIER_TRADE_LIMIT, applyBasicTradeWindow, resolveIsBasicLimitedTier } from '../lib/basicTierLimits';
import { notifyTradesUpdated } from '../utils/tradingAccountEvents';
import { snaptradeService } from './snaptradeService';

const TRADES_COLLECTION = 'trades';
const ACCOUNTS_COLLECTION = 'accounts';
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;
const ACCOUNT_TRADES_CACHE_MS = 15_000;

type TradeType = 'LONG' | 'SHORT';

async function retry<T>(fn: () => Promise<T>, retries = MAX_RETRIES): Promise<T> {
  try {
    return await fn();
  } catch (error: any) {
    if (retries > 0 && error.code === 'unavailable') {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return retry(fn, retries - 1);
    }
    throw error;
  }
}

const convertTradeDoc = (doc: QueryDocumentSnapshot<DocumentData>): Trade => {
  const data = doc.data();
  if (!data) throw new Error('No data in trade document');

  // Pour les trades manuels, utiliser le format standard
  return {
    id: doc.id,
    accountId: data.accountId,
    userId: data.userId,
    openDateTime: data.openDateTime,
    closeDateTime: data.closeDateTime,
    type: data.type,
    asset: data.asset,
    entryPrice: Number(data.entryPrice) || 0,
    exitPrice: Number(data.exitPrice) || 0,
    stopLoss: Number(data.stopLoss) || 0,
    takeProfit: Number(data.takeProfit) || 0,
    riskPercentage: Number(data.riskPercentage) || 1,
    riskRewardRatio: data.riskRewardRatio,
    lotSize: Number(data.lotSize) || 0,
    profitLoss: Number(data.profitLoss) || 0,
    mood: data.mood,
    outcome: data.outcome,
    notes: data.notes || '',
    images: data.images || [],
    planConditions: data.planConditions || {},
    tags: data.tags || {},
    accountBalance: Number(data.accountBalance) || 0,
    timeUnit: data.timeUnit
  };
};

const accountTradesCache = new Map<string, { expiresAt: number; promise: Promise<Trade[]> }>();

const ALL_USER_TRADES_CACHE_MS = 30_000;
const allUserTradesCache = new Map<string, { expiresAt: number; promise: Promise<Trade[]> }>();

const getAccountTradesCacheKey = (userId: string, accountId: string) => `${userId}:${accountId}`;

function cloneTrades(trades: Trade[]): Trade[] {
  return trades.map(trade => ({ ...trade }));
}

// Cache court pour la lecture « tous les trades de l'utilisateur » (mêmes requête/mapping
// que l'ancienne version). Évite de relire jusqu'à 1000 docs à chaque ouverture d'un
// événement du calendrier quand on en parcourt plusieurs d'affilée.
function fetchAllUserTradesCached(userId: string): Promise<Trade[]> {
  const cached = allUserTradesCache.get(userId);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.promise.then(cloneTrades);
  }

  const tradesRef = collection(db, 'trades');
  const q = query(
    tradesRef,
    where('userId', '==', userId),
    limit(1000)
  );

  const promise = getDocs(q).then(snapshot => snapshot.docs.map(docSnap => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      entryPrice: Number(data.entryPrice) || 0,
      exitPrice: Number(data.exitPrice) || 0,
      stopLoss: Number(data.stopLoss) || 0,
      takeProfit: Number(data.takeProfit) || 0,
      lotSize: Number(data.lotSize) || 0,
      profitLoss: Number(data.profitLoss) || 0,
      accountBalance: Number(data.accountBalance) || 0,
      riskPercentage: Number(data.riskPercentage) || 1,
    } as Trade;
  }));

  allUserTradesCache.set(userId, { expiresAt: Date.now() + ALL_USER_TRADES_CACHE_MS, promise });
  promise.catch(() => allUserTradesCache.delete(userId));
  return promise.then(cloneTrades);
}

function clearAccountTradesCache(userId?: string, accountId?: string): void {
  if (!userId) {
    accountTradesCache.clear();
    allUserTradesCache.clear();
    return;
  }

  // Toute écriture sur un trade de l'utilisateur invalide aussi sa lecture user-wide.
  allUserTradesCache.delete(userId);

  if (accountId) {
    accountTradesCache.delete(getAccountTradesCacheKey(userId, accountId));
    return;
  }

  for (const key of accountTradesCache.keys()) {
    if (key.startsWith(`${userId}:`)) {
      accountTradesCache.delete(key);
    }
  }
}

function computeTradeProfitLoss(trade: Trade, lotSize: number): number {
  if (!trade.exitPrice || !trade.asset) return 0;

  try {
    return calculateAssetProfitLoss(
      trade.asset,
      Number(trade.entryPrice),
      Number(trade.exitPrice),
      Number(lotSize),
      trade.type === 'SHORT' ? 'SHORT' : 'LONG',
    );
  } catch {
    return 0;
  }
}

export const tradeService = {
  async getAllUserTrades(userId: string): Promise<Trade[]> {
    return fetchAllUserTradesCached(userId);
  },

  async fetchAllTradesByAccount(accountId: string): Promise<Trade[]> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('Must be logged in to fetch trades');

    const cacheKey = getAccountTradesCacheKey(user.uid, accountId);
    const cached = accountTradesCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      const cachedTrades = await cached.promise;
      return cloneTrades(cachedTrades);
    }

    const tradesRef = collection(db, 'trades');
    const q = query(
      tradesRef,
      where('accountId', '==', accountId),
      where('userId', '==', user.uid)
    );

    const promise = getDocs(q).then(snapshot => snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        entryPrice: Number(docSnap.data().entryPrice) || 0,
        exitPrice: Number(docSnap.data().exitPrice) || 0,
        stopLoss: Number(docSnap.data().stopLoss) || 0,
        takeProfit: Number(docSnap.data().takeProfit) || 0,
        lotSize: Number(docSnap.data().lotSize) || 0,
        profitLoss: Number(docSnap.data().profitLoss) || 0,
        accountBalance: Number(docSnap.data().accountBalance) || 0,
        riskPercentage: Number(docSnap.data().riskPercentage) || 1,
      } as Trade)))
      .catch(error => {
        accountTradesCache.delete(cacheKey);
        throw error;
      });

    accountTradesCache.set(cacheKey, {
      expiresAt: Date.now() + ACCOUNT_TRADES_CACHE_MS,
      promise,
    });

    const trades = await promise;
    return cloneTrades(trades);
  },

  async getTradesByAccount(
    accountId: string,
    options?: { includeAll?: boolean; isLimitedTier?: boolean },
  ): Promise<Trade[]> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Must be logged in to fetch trades');

      const trades = await this.fetchAllTradesByAccount(accountId);

      if (options?.includeAll) {
        return [...trades].sort((a, b) => {
          const dateA = new Date(a.openDateTime).getTime();
          const dateB = new Date(b.openDateTime).getTime();
          return dateB - dateA;
        });
      }

      const isLimitedTier = options?.isLimitedTier ?? await resolveIsBasicLimitedTier(user.uid);
      const { visibleTrades } = applyBasicTradeWindow(trades, isLimitedTier);

      return [...visibleTrades].sort((a, b) => {
        const dateA = new Date(a.openDateTime).getTime();
        const dateB = new Date(b.openDateTime).getTime();
        return dateB - dateA;
      });
    } catch (error: any) {
      secureLog.error('Error fetching trades:', error);
      throw error;
    }
  },

  async getTradeCountsByAccount(
    accountId: string,
    isLimitedTier?: boolean,
  ): Promise<{ totalCount: number; visibleCount: number; hiddenCount: number }> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('Must be logged in to fetch trades');

    const tradesRef = collection(db, 'trades');
    const countQuery = query(
      tradesRef,
      where('accountId', '==', accountId),
      where('userId', '==', user.uid),
    );
    const countSnapshot = await getCountFromServer(countQuery);
    const totalCount = countSnapshot.data().count;
    const limited = isLimitedTier ?? await resolveIsBasicLimitedTier(user.uid);
    const visibleCount = limited ? Math.min(totalCount, BASIC_TIER_TRADE_LIMIT) : totalCount;
    return {
      totalCount,
      visibleCount,
      hiddenCount: Math.max(0, totalCount - visibleCount),
    };
  },

  async getTradesForEventDate(date: string, timeWindow: number = 60): Promise<Trade[]> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Must be logged in to fetch trades');

      // Vérifier si la date est valide
      if (!date) {
        secureLog.error('Date d\'événement manquante');
        return [];
      }

      // Convertir la date de l'événement en objet Date
      const eventDate = new Date(date);

      // Vérifier si la date est valide
      if (isNaN(eventDate.getTime())) {
        secureLog.error(`Date d'événement invalide: ${date}`);
        return [];
      }

      // Calculer la plage de temps (par défaut ±60 minutes)
      const startTime = new Date(eventDate.getTime() - timeWindow * 60000);
      const endTime = new Date(eventDate.getTime() + timeWindow * 60000);

      secureLog.log(`Recherche de trades autour de l'événement du ${eventDate.toLocaleString()}`);
      secureLog.log(`Fenêtre de recherche: ${startTime.toLocaleString()} à ${endTime.toLocaleString()}`);

      // Récupérer tous les trades de l'utilisateur (cache court, dédup des relectures)
      const allTrades = await fetchAllUserTradesCached(user.uid);

      secureLog.log(`Total trades récupérés: ${allTrades.length}`);

      const isLimitedTier = await resolveIsBasicLimitedTier(user.uid);
      const { visibleTrades: tierScopedTrades } = applyBasicTradeWindow(allTrades, isLimitedTier);

      // Fonction pour vérifier si deux dates sont le même jour
      const isSameDay = (date1: Date, date2: Date): boolean => {
        return date1.getFullYear() === date2.getFullYear() &&
          date1.getMonth() === date2.getMonth() &&
          date1.getDate() === date2.getDate();
      };

      // Fonction pour vérifier si une date est entre deux autres dates (inclusivement)
      const isDateBetween = (date: Date, start: Date, end: Date): boolean => {
        return date.getTime() >= start.getTime() && date.getTime() <= end.getTime();
      };

      // Filtrer les trades qui se sont produits autour de la date de l'événement
      const relatedTrades = tierScopedTrades.filter(trade => {
        try {
          // Vérifier si le trade a des dates d'ouverture/fermeture
          if (!trade.openDateTime) {
            return false;
          }

          // Convertir les dates de string à Date
          const openTime = new Date(trade.openDateTime);
          const closeTime = trade.closeDateTime ? new Date(trade.closeDateTime) : null;

          // Vérifier si les dates sont valides
          if (isNaN(openTime.getTime()) || (closeTime && isNaN(closeTime.getTime()))) {
            return false;
          }

          // Vérifier si les dates sont dans la même année
          const sameYear = openTime.getFullYear() === eventDate.getFullYear() ||
            (closeTime && closeTime.getFullYear() === eventDate.getFullYear());

          if (!sameYear) {
            return false;
          }

          // Un trade est lié à l'événement si:
          // 1. Il a été ouvert pendant la fenêtre de temps de l'événement
          const openedDuringEvent = isDateBetween(openTime, startTime, endTime);

          // 2. Il a été fermé pendant la fenêtre de temps de l'événement
          const closedDuringEvent = closeTime && isDateBetween(closeTime, startTime, endTime);

          // 3. L'événement s'est produit pendant que le trade était ouvert
          // (le trade était ouvert avant l'événement et fermé après, ou toujours ouvert)
          const eventDuringTrade = openTime <= startTime &&
            (closeTime ? closeTime >= endTime : false);

          const isRelated = openedDuringEvent || closedDuringEvent || eventDuringTrade;

          if (isRelated) {
            secureLog.log(`Trade ${trade.id} associé à l'événement - Ouvert: ${openTime.toLocaleString()}, Fermé: ${closeTime?.toLocaleString() || 'N/A'}`);
          }

          return isRelated;
        } catch (error) {
          secureLog.error(`Erreur lors du filtrage du trade ${trade.id}:`, error);
          return false;
        }
      });

      secureLog.log(`Trades associés trouvés: ${relatedTrades.length}`);

      // Trier par date d'ouverture (plus récent en premier)
      return relatedTrades.sort((a, b) => {
        try {
          const dateA = new Date(a.openDateTime || '').getTime();
          const dateB = new Date(b.openDateTime || '').getTime();
          if (isNaN(dateA) || isNaN(dateB)) return 0;
          return dateB - dateA;
        } catch (error) {
          secureLog.error('Erreur lors du tri des trades:', error);
          return 0;
        }
      });

    } catch (error: any) {
      secureLog.error('Error fetching trades for event:', error);
      throw error;
    }
  },

  async createTrade(trade: Omit<Trade, 'id'>, currentBalance: number): Promise<string> {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) throw new Error('Must be logged in to create trade');
    if (!trade.accountId) throw new Error('Account ID is required');

    try {
      const tradeId = await runTransaction(db, async (transaction) => {
        // Récupérer le compte
        const accountRef = doc(db, ACCOUNTS_COLLECTION, trade.accountId);
        const accountDoc = await transaction.get(accountRef);

        if (!accountDoc.exists()) {
          throw new Error('Account not found');
        }

        const account = accountDoc.data();
        const accountBalance = account.currentBalance || account.initialBalance;

        // Utiliser le RR réalisé fourni par l'utilisateur s'il existe,
        // sinon calculer le RR prévu à partir du Take Profit uniquement en fallback
        let riskRewardRatio = trade.riskRewardRatio ? String(trade.riskRewardRatio) : '';
        if (!riskRewardRatio && trade.takeProfit && trade.stopLoss) {
          riskRewardRatio = calculateRiskRewardRatio(
            trade.entryPrice,
            trade.takeProfit,
            trade.stopLoss,
            trade.type === 'LONG'
          );
        }

        // Utiliser la lotSize fournie par l'utilisateur, sinon calculer automatiquement
        // seulement si tous les paramètres nécessaires sont présents
        let lotSize = trade.lotSize;
        if ((!lotSize || lotSize === 0) && trade.stopLoss && trade.riskPercentage && trade.entryPrice && accountBalance) {
          try {
            lotSize = calculateLotSize(
              trade.asset,
              trade.entryPrice,
              trade.stopLoss,
              trade.riskPercentage,
              accountBalance,
              'USD'
            );
          } catch {
            lotSize = 0;
          }
        } else if (!lotSize) {
          lotSize = 0;
        }

        // Calculer le P/L si le trade est fermé
        let profitLoss = 0;
        if (trade.exitPrice) {
          // Utiliser directement la valeur du profitLossStr si elle existe
          profitLoss = trade.profitLossStr ? Number(trade.profitLossStr) : computeTradeProfitLoss({
            ...trade,
            lotSize: Number(lotSize),
            entryPrice: Number(trade.entryPrice),
            exitPrice: Number(trade.exitPrice),
          } as Trade, Number(lotSize));
        }

        // Générer l'ID du trade
        const tradeRef = doc(collection(db, TRADES_COLLECTION));
        const tradeId = tradeRef.id;

        // Nettoyer les données du trade pour Firebase
        const cleanTrade = { ...trade };

        // Supprimer les images et médias pour le moment, ils seront ajoutés après
        delete cleanTrade.images;
        delete cleanTrade.media;

        // Convertir les undefined en null
        // Convertir undefined en null ET NaN/Infinity en 0 pour éviter les erreurs Firestore
        Object.keys(cleanTrade).forEach(key => {
          if (cleanTrade[key] === undefined) {
            cleanTrade[key] = null;
          } else if (typeof cleanTrade[key] === 'number' && (isNaN(cleanTrade[key]) || !isFinite(cleanTrade[key]))) {
            cleanTrade[key] = 0;
          }
        });

        // Calculer la running balance pour ce nouveau trade (inclut P&L + commission + swap)
        const commission = (typeof trade.commission === 'number' && !isNaN(trade.commission)) ? trade.commission : 0;
        const swap = (typeof trade.swap === 'number' && !isNaN(trade.swap)) ? trade.swap : 0;
        const totalImpact = profitLoss + commission + swap;
        const runningBalance = await runningBalanceService.calculateRunningBalanceForNewTrade(
          trade.accountId,
          trade.openDateTime || new Date().toISOString(),
          totalImpact
        );

        // Ajouter les champs calculés
        cleanTrade.userId = user.uid;
        cleanTrade.date = Timestamp.fromDate(new Date());
        cleanTrade.createdAt = Timestamp.now();
        cleanTrade.riskRewardRatio = riskRewardRatio;
        cleanTrade.lotSize = lotSize;
        cleanTrade.profitLoss = profitLoss;
        cleanTrade.accountBalance = accountBalance;
        cleanTrade.runningBalance = runningBalance; // ← NOUVEAU CHAMP

        // Créer le trade sans les images d'abord
        transaction.set(tradeRef, cleanTrade);

        // Mettre à jour le solde du compte si le trade est fermé
        if (trade.exitPrice) {
          const newBalance = accountBalance + profitLoss;
          transaction.update(accountRef, {
            currentBalance: newBalance,
            updatedAt: Timestamp.now()
          });
        }

        return tradeId;
      });

      // Après la transaction, traiter les médias et les images
      if (trade.media && Array.isArray(trade.media) && trade.media.length > 0) {
        try {
          // Préserver les descriptions lors de l'upload des médias
          const mediaWithDescriptions: any[] = [];

          trade.media.forEach((media: any) => {
            if (media && typeof media === 'object' && 'url' in media) {
              // Média avec URL (pas encore uploadé mais avec description)
              mediaWithDescriptions.push({
                mediaData: media,
                description: media.description || ''
              });
            } else {
              // Fichier ou base64 brut
              mediaWithDescriptions.push({
                mediaData: media,
                description: ''
              });
            }
          });

          const mediaToUpload = mediaWithDescriptions.map(item => item.mediaData);
          const uploadedMedia = await mediaStorageService.uploadTradeMedias(user.uid, tradeId, mediaToUpload);

          // ✅ FIX: Mapper les descriptions AVANT de filtrer les nulls pour préserver l'alignement des index
          const finalMedia = uploadedMedia
            .map((media, index) => media ? ({
              ...media,
              description: mediaWithDescriptions[index]?.description || ''
            }) : null)
            .filter((item): item is NonNullable<typeof item> => item !== null);

          // Mettre à jour le trade avec les URLs des médias uploadés
          const tradeRef = doc(db, TRADES_COLLECTION, tradeId);
          await updateDoc(tradeRef, { media: finalMedia });
        } catch (error) {
          secureLog.error('Error uploading media:', error);
          // Ne pas faire échouer la création du trade si l'upload des médias échoue
        }
      }

      if (trade.images && Array.isArray(trade.images) && trade.images.length > 0) {
        try {
          const uploadedImages = await mediaStorageService.uploadTradeImages(user.uid, tradeId, trade.images);

          // Mettre à jour le trade avec les URLs des images uploadées
          const tradeRef = doc(db, TRADES_COLLECTION, tradeId);
          await updateDoc(tradeRef, { images: uploadedImages });
        } catch (error) {
          secureLog.error('Error uploading images:', error);
          // Ne pas faire échouer la création du trade si l'upload des images échoue
        }
      }

      clearAccountTradesCache(user.uid, trade.accountId);
      notifyTradesUpdated(trade.accountId);
      return tradeId;
    } catch (error) {
      secureLog.error('Error creating trade:', error);
      throw error;
    }
  },

  async updateTradeField(tradeId: string, field: string, value: any): Promise<void> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Must be logged in to update trade');

      const tradeRef = doc(db, TRADES_COLLECTION, tradeId);

      // Sanitize: NaN/Infinity → 0 pour Firestore
      const safeValue = (typeof value === 'number' && (isNaN(value) || !isFinite(value))) ? 0 : value;

      // Mise à jour simple d'un seul champ
      await updateDoc(tradeRef, {
        [field]: safeValue
      });
      clearAccountTradesCache(user.uid);
    } catch (error: any) {
      secureLog.error('Error updating trade field:', error);
      throw new Error(`Error updating trade field: ${error.message}`);
    }
  },

  async updateTrade(trade: Trade): Promise<void> {
    try {
      console.log('🔄 [TradeService] Début updateTrade pour trade:', trade.id);

      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        console.error('❌ [TradeService] Utilisateur non connecté');
        throw new Error('Must be logged in to update trade');
      }

      console.log('✅ [TradeService] Utilisateur connecté:', {
        uid: user.uid,
        email: user.email
      });

      const tradeRef = doc(db, TRADES_COLLECTION, trade.id);
      console.log('📍 [TradeService] Référence Firestore:', {
        collection: TRADES_COLLECTION,
        tradeId: trade.id,
        path: `${TRADES_COLLECTION}/${trade.id}`
      });

      // Récupérer l'ancien trade pour comparer les P&L
      console.log('📖 [TradeService] Lecture du trade existant...');
      const oldTradeDoc = await getDoc(tradeRef);
      if (!oldTradeDoc.exists()) {
        console.error('❌ [TradeService] Trade non trouvé dans Firestore');
        throw new Error('Trade non trouvé');
      }
      console.log('✅ [TradeService] Trade existant trouvé');

      const oldTradeData = oldTradeDoc.data() as Trade;
      console.log('📊 [TradeService] Données du trade existant:', {
        userId: oldTradeData.userId,
        accountId: oldTradeData.accountId,
        profitLoss: oldTradeData.profitLoss
      });

      const oldPnL = oldTradeData.profitLoss || 0;

      // ✅ CORRECTION: Fonction pour nettoyer un objet média (supprimer les propriétés undefined et file)
      const cleanMediaArray = (mediaArray: any[]): any[] => {
        if (!Array.isArray(mediaArray)) return [];
        return mediaArray
          .filter(item => item != null) // Filtrer les null/undefined
          .map(item => {
            const cleaned: any = {};
            // Ne garder que les propriétés définies et exclure 'file' (non sérialisable)
            if (item.url !== undefined && item.url !== null) cleaned.url = item.url;
            if (item.type !== undefined && item.type !== null) cleaned.type = item.type;
            if (item.description !== undefined && item.description !== null) cleaned.description = item.description;
            if (item.size !== undefined && item.size !== null) cleaned.size = item.size;
            if (item.name !== undefined && item.name !== null) cleaned.name = item.name;
            return cleaned;
          })
          .filter(item => Object.keys(item).length > 0); // Filtrer les objets vides
      };

      // Liste des champs de médias academy
      const academyMediaFields = [
        'academyDailyMedia', 'academyH4Media', 'academyPositionMedia',
        'academyDailyAfterMedia', 'academyH4AfterMedia', 'academyPositionAfterMedia'
      ];

      // Convertir les valeurs undefined en null pour Firebase
      console.log('🧹 [TradeService] Nettoyage des données du trade...');
      const cleanTrade = Object.entries(trade).reduce((acc, [key, value]) => {
        // Ignorer l'ID du document (Firestore ne l'accepte pas dans les mises à jour)
        if (key === 'id') return acc;

        // Ignorer les propriétés images et media pour le moment
        if (key === 'images' || key === 'media') return acc;

        // ✅ CORRECTION: Traitement spécial pour les tableaux de médias academy
        if (academyMediaFields.includes(key)) {
          const cleanedMedia = cleanMediaArray(value as any[]);
          // Sauvegarder le tableau même s'il est vide (pour supprimer les anciennes données)
          acc[key] = cleanedMedia;
          return acc;
        }

        // Traitement spécial pour les dates : chaînes vides deviennent null
        if ((key === 'openDateTime' || key === 'closeDateTime') && value === '') {
          acc[key] = null;
        } else if (value === undefined) {
          acc[key] = null;
        } else if (typeof value === 'number' && (isNaN(value) || !isFinite(value))) {
          acc[key] = 0;
        } else {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>);
      // Le propriétaire ne change jamais : un userId vide (undefined → null ci-dessus)
      // rendrait le trade orphelin, et les règles Firestore refusent tout changement de userId.
      if (cleanTrade.userId == null) delete cleanTrade.userId;

      console.log('✅ [TradeService] Données nettoyées:', {
        nombreDeChamps: Object.keys(cleanTrade).length,
        champs: Object.keys(cleanTrade),
        userId: cleanTrade.userId,
        accountId: cleanTrade.accountId
      });

      // Debug pour voir les dates nettoyées
      console.log('📅 [TradeService] Dates nettoyées:', {
        openDateTime: cleanTrade.openDateTime,
        closeDateTime: cleanTrade.closeDateTime,
        originalOpenDateTime: trade.openDateTime,
        originalCloseDateTime: trade.closeDateTime
      });

      // Si les champs Real sont explicitement définis à null, les supprimer de la base de données
      // Cela permet de forcer l'utilisation des champs standard quand les dates sont modifiées manuellement
      if (cleanTrade.openDateTimeReal === null) {
        cleanTrade.openDateTimeReal = deleteField(); // Supprimer complètement le champ
      }
      if (cleanTrade.closeDateTimeReal === null) {
        cleanTrade.closeDateTimeReal = deleteField(); // Supprimer complètement le champ
      }

      // ✅ Si planConditions est undefined, null ou un objet vide, supprimer le champ de Firebase
      if (cleanTrade.planConditions === null ||
        cleanTrade.planConditions === undefined ||
        (typeof cleanTrade.planConditions === 'object' && Object.keys(cleanTrade.planConditions).length === 0)) {
        cleanTrade.planConditions = deleteField(); // Supprimer complètement le champ
      }

      // S'assurer que riskRewardRatio est une chaîne
      if (cleanTrade.riskRewardRatio !== null && cleanTrade.riskRewardRatio !== undefined) {
        cleanTrade.riskRewardRatio = String(cleanTrade.riskRewardRatio);
      }

      // Calculer le P/L si le trade est fermé
      if (cleanTrade.exitPrice) {
        cleanTrade.profitLoss = trade.profitLossStr ? Number(trade.profitLossStr) : computeTradeProfitLoss({
          ...trade,
          ...cleanTrade,
          lotSize: Number(cleanTrade.lotSize),
          entryPrice: Number(cleanTrade.entryPrice),
          exitPrice: Number(cleanTrade.exitPrice),
        } as Trade, Number(cleanTrade.lotSize));
      }

      // Calculer les nouveaux impacts pour détecter les changements
      const newPnL = (typeof cleanTrade.profitLoss === 'number' && !isNaN(cleanTrade.profitLoss)) ? cleanTrade.profitLoss : 0;
      const newCommission = (typeof cleanTrade.commission === 'number' && !isNaN(cleanTrade.commission)) ? cleanTrade.commission : 0;
      const newSwap = (typeof cleanTrade.swap === 'number' && !isNaN(cleanTrade.swap)) ? cleanTrade.swap : 0;
      const oldCommission = (typeof oldTradeData.commission === 'number' && !isNaN(oldTradeData.commission)) ? oldTradeData.commission : 0;
      const oldSwap = (typeof oldTradeData.swap === 'number' && !isNaN(oldTradeData.swap)) ? oldTradeData.swap : 0;
      const oldTotal = oldPnL + oldCommission + oldSwap;
      const newTotal = newPnL + newCommission + newSwap;

      // Mettre à jour le trade sans les médias d'abord
      console.log('💾 [TradeService] Tentative de mise à jour Firestore...');
      console.log('📦 [TradeService] Données envoyées à Firestore:', JSON.stringify(cleanTrade, null, 2));

      try {
        await updateDoc(tradeRef, cleanTrade);
        console.log('✅ [TradeService] Mise à jour Firestore réussie !');
      } catch (firestoreError: any) {
        console.error('❌ [TradeService] ERREUR FIRESTORE:', {
          code: firestoreError.code,
          message: firestoreError.message,
          name: firestoreError.name,
          stack: firestoreError.stack,
          tradeId: trade.id,
          userId: user.uid,
          cleanTradeUserId: cleanTrade.userId,
          oldTradeUserId: oldTradeData.userId
        });
        throw new Error(`Error updating trade: ${firestoreError.message}`);
      }

      // Mettre à jour le solde du compte si le P&L a changé (ex. finalisation d'un brouillon)
      // Aligné sur createTrade : seul profitLoss impacte currentBalance
      const oldContribution = oldTradeData.exitPrice ? oldPnL : 0;
      const newContribution = cleanTrade.exitPrice ? newPnL : 0;
      const balanceDelta = newContribution - oldContribution;

      if (balanceDelta !== 0 && trade.accountId) {
        try {
          const accountRef = doc(db, ACCOUNTS_COLLECTION, trade.accountId);
          await runTransaction(db, async (transaction) => {
            const accountDoc = await transaction.get(accountRef);
            if (!accountDoc.exists()) return;
            const accountBalance = accountDoc.data().currentBalance ?? accountDoc.data().initialBalance ?? 0;
            transaction.update(accountRef, {
              currentBalance: accountBalance + balanceDelta,
              updatedAt: Timestamp.now(),
            });
          });
        } catch (balanceError) {
          secureLog.error('Error updating account balance after trade update:', balanceError);
        }
      }

      // Mettre à jour la running balance en cascade si l'impact total a changé
      if (newTotal !== oldTotal && trade.accountId) {
        // console.log supprimé

        // ⚡ OPTIMISATION: Mise à jour asynchrone non-bloquante
        // L'utilisateur n'attend pas cette opération
        const taskId = `running_balance_${trade.id}_${Date.now()}`;

        backgroundTaskService.executeTask(
          taskId,
          `Mise à jour running balance (${Math.abs(newTotal - oldTotal).toFixed(2)} USD impact)`,
          () => runningBalanceService.updateRunningBalanceFromTrade(
            trade.accountId,
            trade.id,
            { profitLoss: oldPnL, commission: oldCommission, swap: oldSwap },
            { profitLoss: newPnL, commission: newCommission, swap: newSwap }
          )
        ).catch(error => {
          // Erreur non-critique, n'affecte pas l'utilisateur
          // console.warn supprimé
        });
      }

      // Récupérer le trade existant pour préserver les médias actuels
      const existingTradeDoc = await getDoc(tradeRef);
      const existingTrade = existingTradeDoc.data();
      const existingMedia = existingTrade?.media || [];

      // ✅ CORRECTION: Collecter TOUTES les URLs des médias academy pour éviter de les supprimer par erreur
      const academyMediaUrls = new Set<string>();
      const academyFields = ['academyDailyMedia', 'academyH4Media', 'academyPositionMedia', 'academyDailyAfterMedia', 'academyH4AfterMedia', 'academyPositionAfterMedia'];
      academyFields.forEach(field => {
        const fieldData = (trade as any)[field] || [];
        fieldData.forEach((item: any) => {
          if (item?.url && item.url.includes('firebasestorage.googleapis.com')) {
            academyMediaUrls.add(item.url);
          }
        });
      });
      console.log('🔒 [TradeService] URLs academy protégées:', academyMediaUrls.size);

      // Traiter les médias séparément - UTILISER seulement les médias du formulaire comme source de vérité
      if (trade.media && Array.isArray(trade.media)) {
        try {
          // Séparer les médias existants des nouveaux
          const newMediaWithDescriptions: any[] = [];
          const existingMediaItems: any[] = [];

          trade.media.forEach((media: any) => {
            if (media && typeof media === 'object' && 'url' in media) {
              if (media.url.includes('firebasestorage.googleapis.com')) {
                // Média existant - garder tel quel
                existingMediaItems.push(media);
              } else {
                // Nouveau média (base64) - préserver la description
                newMediaWithDescriptions.push({
                  mediaData: media,
                  description: media.description || ''
                });
              }
            } else {
              // Nouveau fichier ou base64 - pas de description
              newMediaWithDescriptions.push({
                mediaData: media,
                description: ''
              });
            }
          });

          // ✅ CORRECTION: Commencer avec SEULEMENT les médias du formulaire, pas ceux de la DB
          let finalMedia = [...existingMediaItems];

          // Supprimer les médias qui ne sont plus dans le formulaire
          const mediaUrlsInForm = existingMediaItems.map(item => item.url);
          const mediaToDelete = existingMedia.filter((existingItem: any) =>
            existingItem.url &&
            existingItem.url.includes('firebasestorage.googleapis.com') &&
            !mediaUrlsInForm.includes(existingItem.url) &&
            !academyMediaUrls.has(existingItem.url) // ✅ Ne pas supprimer les médias academy !
          );

          // Supprimer les médias orphelins de Firebase Storage
          console.log('🗑️ [TradeService] Médias à supprimer:', mediaToDelete.length, mediaToDelete.map((m: any) => m.url?.substring(0, 80)));
          for (const mediaToDeleteItem of mediaToDelete) {
            try {
              // ✅ Double vérification: ne pas supprimer les médias academy
              if (academyMediaUrls.has(mediaToDeleteItem.url)) {
                console.log('🔒 [TradeService] Média academy protégé, skip suppression:', mediaToDeleteItem.url?.substring(0, 80));
                continue;
              }
              console.log('🗑️ [TradeService] Suppression média:', mediaToDeleteItem.url?.substring(0, 80));
              await mediaStorageService.deleteMediaFromStorage(mediaToDeleteItem.url);
            } catch (error) {
              // Ne pas faire échouer si la suppression échoue
              secureLog.warn('Failed to delete media from storage:', error);
            }
          }

          // Uploader les nouveaux médias et les ajouter
          if (newMediaWithDescriptions.length > 0) {
            const mediaToUpload = newMediaWithDescriptions.map(item => item.mediaData);
            const uploadedMedia = await mediaStorageService.uploadTradeMedias(user.uid, trade.id, mediaToUpload);

            // ✅ FIX: Mapper les descriptions AVANT de filtrer les nulls pour préserver l'alignement des index
            const uploadedWithDescriptions = uploadedMedia
              .map((media, index) => media ? ({
                ...media,
                description: newMediaWithDescriptions[index]?.description || ''
              }) : null)
              .filter((item): item is NonNullable<typeof item> => item !== null);

            finalMedia = [...finalMedia, ...uploadedWithDescriptions];
          }

          await updateDoc(tradeRef, { media: finalMedia });
        } catch (error) {
          secureLog.error('Error uploading media during update:', error);
          // Ne pas faire échouer la mise à jour du trade si l'upload des médias échoue
        }
      } else {
        // Si aucun média dans le formulaire, supprimer tous les médias existants
        console.log('🗑️ [TradeService] Aucun média dans formulaire, suppression de tous les existants:', existingMedia.length);
        try {
          for (const existingItem of existingMedia) {
            if (existingItem.url && existingItem.url.includes('firebasestorage.googleapis.com')) {
              // ✅ Ne pas supprimer les médias academy !
              if (academyMediaUrls.has(existingItem.url)) {
                console.log('🔒 [TradeService] Média academy protégé, skip suppression:', existingItem.url?.substring(0, 80));
                continue;
              }
              try {
                console.log('🗑️ [TradeService] Suppression complète média:', existingItem.url?.substring(0, 80));
                await mediaStorageService.deleteMediaFromStorage(existingItem.url);
              } catch (error) {
                secureLog.warn('Failed to delete media from storage during complete removal:', error);
              }
            }
          }
          await updateDoc(tradeRef, { media: [] });
        } catch (error) {
          secureLog.error('Error removing all media:', error);
        }
      }

      // Traiter les images séparément - PRÉSERVER les existantes et ajouter les nouvelles
      if (trade.images && Array.isArray(trade.images) && trade.images.length > 0) {
        try {
          // Récupérer les images existantes
          const existingImages = existingTrade?.images || [];

          // Séparer les images existantes des nouvelles
          const newImages: any[] = [];
          const existingImageUrls: string[] = [];

          trade.images.forEach((img: any) => {
            if (typeof img === 'string') {
              if (img.includes('firebasestorage.googleapis.com')) {
                // Image existante
                existingImageUrls.push(img);
              } else {
                // Nouvelle image (base64)
                newImages.push(img);
              }
            } else if (typeof img === 'object' && 'url' in img) {
              if (img.url.includes('firebasestorage.googleapis.com')) {
                // Image existante
                existingImageUrls.push(img.url);
              } else {
                // Nouvelle image (base64)
                newImages.push(img);
              }
            } else {
              // Nouveau fichier
              newImages.push(img);
            }
          });

          // Commencer avec les images existantes (du DB)
          let finalImages = [...existingImages.filter(img => typeof img === 'string' ? existingImageUrls.includes(img) : existingImageUrls.includes(img.url))];

          // Uploader les nouvelles images et les ajouter
          if (newImages.length > 0) {
            const uploadedImages = await mediaStorageService.uploadTradeImages(user.uid, trade.id, newImages);
            // Ajouter les nouvelles images au format string (pour compatibilité avec le champ images)
            const newImageUrls = uploadedImages.map(img => img.url);
            finalImages = [...finalImages, ...newImageUrls];
          }

          await updateDoc(tradeRef, { images: finalImages });
        } catch (error) {
          secureLog.error('Error uploading images during update:', error);
          // Ne pas faire échouer la mise à jour du trade si l'upload des images échoue
        }
      }
      clearAccountTradesCache(user.uid, oldTradeData.accountId);
      if (trade.accountId && trade.accountId !== oldTradeData.accountId) {
        clearAccountTradesCache(user.uid, trade.accountId);
      }
      notifyTradesUpdated(trade.accountId ?? oldTradeData.accountId);
    } catch (error: any) {
      secureLog.error('Error updating trade:', error);
      throw new Error(`Error updating trade: ${error.message}`);
    }
  },

  async deleteTradesByAccount(accountId: string): Promise<void> {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) throw new Error('Must be logged in to delete trades');
    if (!accountId) throw new Error('Account ID is required');

    try {
      // Récupérer tous les trades du compte
      const tradesQuery = query(
        collection(db, TRADES_COLLECTION),
        where('accountId', '==', accountId),
        where('userId', '==', user.uid)
      );

      const tradesSnapshot = await getDocs(tradesQuery);

      // Supprimer chaque trade par lots (limite Firestore : 500 opérations par batch)
      const BATCH_LIMIT = 400;
      const tradeDocs = tradesSnapshot.docs;
      for (let i = 0; i < tradeDocs.length; i += BATCH_LIMIT) {
        const batch = writeBatch(db);
        tradeDocs.slice(i, i + BATCH_LIMIT).forEach((tradeDoc) => {
          batch.delete(tradeDoc.ref);
        });
        await batch.commit();
      }
      clearAccountTradesCache(user.uid, accountId);
    } catch (error) {
      secureLog.error('Error deleting trades:', error);
      throw error;
    }
  },

  async getMostTradedSymbols(accountId: string): Promise<string[]> {
    const user = getAuth().currentUser;
    if (!user) throw new Error('Must be logged in');
    try {
      const q = query(
        collection(db, 'trades'),
        where('accountId', '==', accountId),
        where('userId', '==', user.uid)
      );
      const querySnapshot = await getDocs(q);

      // Count occurrences of each symbol
      const symbolCounts = querySnapshot.docs.reduce((acc, doc) => {
        const trade = doc.data();
        // Normalize the symbol format to match the retail data format
        const symbol = trade.asset?.toUpperCase().replace('/', '') || '';
        if (symbol) {
          acc[symbol] = (acc[symbol] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>);

      // Sort symbols by count and get top 5
      const sortedSymbols = Object.entries(symbolCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([symbol]) => symbol);

      secureLog.log('Most traded symbols:', sortedSymbols); // Debug log

      return sortedSymbols;
    } catch (error) {
      secureLog.error('Error getting most traded symbols:', error);
      return [];
    }
  },

  async refreshBrokerTrades(accountId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error('Utilisateur non connecté');
      }

      // Récupérer les infos du compte pour avoir la plateforme
      const accountRef = doc(db, ACCOUNTS_COLLECTION, accountId);
      const accountDoc = await getDoc(accountRef);
      const accountData = accountDoc.data();

      if (!accountData) {
        throw new Error('Compte non trouvé');
      }

      // Comptes SnapTrade (~40 courtiers agrégés) : la synchro est un appel REST
      // synchrone côté proxy-server (POST /snaptrade/accounts/:id/sync), pas une
      // tâche de fond à interroger. On court-circuite donc la file
      // `accountNeedUpdate` ci-dessous, propre à MT4/MT5/cTrader/TradeLocker/Tradovate.
      if (accountData.platform === 'snaptrade') {
        try {
          const result = await snaptradeService.syncAccount(accountId);
          if (result?.error) {
            return { success: false, error: result.error };
          }
          return { success: true };
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'Une erreur est survenue lors de la synchronisation SnapTrade',
          };
        }
      }

      // Déterminer la plateforme
      const platform = accountData.metaTrader
        ? accountData.metaTrader.platform || 'mt4'  // Si platform n'est pas défini, on utilise mt4 par défaut
        : accountData.platform === 'ctrader'
          ? 'ctrader'
          : accountData.platform === 'tradelocker'
            ? 'tradelocker'
            : accountData.platform === 'tradovate'
              ? 'tradovate'
              : null;

      if (!platform) {
        throw new Error('Plateforme non supportée');
      }

      // Jeton Firebase obligatoire : le worker le vérifie pour authentifier la demande.
      // Document lisible uniquement par son propriétaire (règles accountNeedUpdate).
      const token = await user.getIdToken();

      const accountNeedUpdateRef = collection(db, 'accountNeedUpdate');
      const docRef = await addDoc(accountNeedUpdateRef, {
        accountId: accountId,
        userId: user.uid,
        status: 'pending',
        requestedAt: new Date(),
        processing: false,
        platform: platform,
        token: token,
      });

      // Retourner une promesse qui sera résolue quand la mise à jour sera terminée
      return new Promise((resolve) => {
        let timeout: NodeJS.Timeout;
        let unsubscribe: () => void = () => {};
        let settled = false;
        // Passe à true dès qu'un snapshot a pu être lu. Si la lecture est refusée
        // APRÈS coup, c'est que le worker a supprimé la demande (la synchro est
        // donc terminée) : les règles ne peuvent plus vérifier resource.data.userId.
        let hasReadDocOnce = false;

        const settle = (result: { success: boolean; error?: string }) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout); // Annuler le timeout
          unsubscribe(); // Arrêter l'écoute
          resolve(result);
        };

        unsubscribe = onSnapshot(docRef, (snapshot) => {
          hasReadDocOnce = true;

          // Si le document n'existe plus, la mise à jour est terminée avec succès
          if (!snapshot.exists()) {
            settle({ success: true });
            return;
          }

          const status = snapshot.data()?.status;

          // Si le document a un statut 'completed', c'est aussi un succès
          if (status === 'completed') {
            settle({ success: true });
          }
          // Si le document a un statut 'error', il y a eu une erreur
          else if (status === 'error') {
            settle({
              success: false,
              error: snapshot.data()?.error || undefined
            });
          }
          // 'pending' / processing: true / autres statuts : on continue d'attendre
        }, (error: any) => {
          // Le worker supprime la demande une fois la synchro terminée. Tant que les
          // règles refusent la lecture d'un document inexistant, cette suppression
          // remonte ici en 'permission-denied' alors que la synchro a réussi.
          if (error?.code === 'permission-denied' && hasReadDocOnce) {
            settle({ success: true });
            return;
          }
          secureLog.error('Error listening to accountNeedUpdate:', error);
          settle({ success: false });
        });

        // Timeout après 240 secondes (4 minutes)
        timeout = setTimeout(() => {
          settle({
            success: false,
            error: 'La mise à jour a pris trop de temps, veuillez réessayer'
          });
        }, 240000); // 4 minutes
      });

    } catch (error) {
      secureLog.error('Error refreshing broker trades:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Une erreur est survenue lors de la mise à jour des trades'
      };
    }
  },

  async getTradeById(tradeId: string): Promise<Trade | null> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Must be logged in to fetch trade');

      const tradeRef = doc(db, 'trades', tradeId);
      const tradeSnapshot = await getDoc(tradeRef);

      if (!tradeSnapshot.exists()) {
        return null;
      }

      const tradeData = tradeSnapshot.data();

      // Vérifier que l'utilisateur a le droit d'accéder à ce trade
      if (tradeData.userId !== user.uid) {
        throw new Error('Access denied to this trade');
      }

      return {
        id: tradeSnapshot.id,
        ...tradeData,
        entryPrice: Number(tradeData.entryPrice) || 0,
        exitPrice: Number(tradeData.exitPrice) || 0,
        stopLoss: Number(tradeData.stopLoss) || 0,
        takeProfit: Number(tradeData.takeProfit) || 0,
        lotSize: Number(tradeData.lotSize) || 0,
        profitLoss: Number(tradeData.profitLoss) || 0,
        accountBalance: Number(tradeData.accountBalance) || 0,
        riskPercentage: Number(tradeData.riskPercentage) || 1,
      } as Trade;

    } catch (error: any) {
      secureLog.error('Error fetching trade by ID:', error);
      throw error;
    }
  }
};
